// Excel workbook integration module

/**
 * Gets Excel metadata (selected range and active worksheet name)
 * @returns Promise with object containing range (e.g., "A1:B11") and activeWorksheet (e.g., "Sales")
 */
export async function getExcelMetadata(): Promise<{ range: string; activeWorksheet: string }> {
    return new Promise((resolve, reject) => {
        if (typeof Excel === 'undefined') {
            reject(new Error("Excel API not available"));
            return;
        }

        Excel.run(async (context) => {
            try {
                // Get the selected range
                const selectedRange = context.workbook.getSelectedRange();
                
                // Get the active worksheet
                const worksheet = context.workbook.worksheets.getActiveWorksheet();
                
                // Load properties
                selectedRange.load(["address"]);
                worksheet.load(["name"]);
                await context.sync();
                
                // Extract range from address (e.g., "Sheet1!A1:B11" -> "A1:B11")
                let range = "";
                const address = selectedRange.address || "";
                if (address.includes('!')) {
                    // Extract the range part after the exclamation mark
                    range = address.split('!')[1];
                } else {
                    // If no exclamation mark, use the address as-is
                    range = address;
                }
                
                const activeWorksheet = worksheet.name || "";
                
                console.log(`[Excel] Metadata - Range: ${range}, ActiveWorksheet: ${activeWorksheet}`);
                resolve({ range, activeWorksheet });
            } catch (error) {
                console.error("[Excel] Error getting metadata:", error);
                // Return empty values on error
                resolve({ range: "", activeWorksheet: "" });
            }
        });
    });
}

export async function getExcelWorkbook(): Promise<string> {
    return new Promise((resolve, reject) => {
        if (typeof Excel === 'undefined') {
            reject(new Error("Excel API not available"));
            return;
        }

        Excel.run(async (context) => {
            try {
                console.log(`[Excel] Starting Excel.run execution`);
                
                // Get the active worksheet
                const worksheet = context.workbook.worksheets.getActiveWorksheet();
                worksheet.load("name");
                await context.sync();
                
                console.log(`[Excel] Active worksheet: ${worksheet.name}`);
                
                // Get the used range (all cells with data)
                const usedRange = worksheet.getUsedRange();
                
                // Check if there's any data
                if (!usedRange) {
                    console.log(`[Excel] No used range found, worksheet is empty`);
                    resolve(`Worksheet: ${worksheet.name}\n\n(Empty worksheet)`);
                    return;
                }
                
                // Load the values from the used range
                usedRange.load("values");
                await context.sync();
                
                console.log(`[Excel] Used range loaded, rows: ${usedRange.values.length}, cols: ${usedRange.values[0]?.length || 0}`);
                
                // Format the data as a readable table
                const formattedData = formatExcelData(worksheet.name, usedRange.values);
                
                console.log(`[Excel] Excel content retrieved, length: ${formattedData.length}`);
                resolve(formattedData);
            } catch (error) {
                console.error("[Excel] Fatal Error:", error);
                console.error("[Excel] Error details:", {
                    error: error,
                    errorMessage: error instanceof Error ? error.message : String(error),
                    errorStack: error instanceof Error ? error.stack : undefined,
                    errorName: error instanceof Error ? error.name : typeof error
                });
                reject(error);
            }
        });
    });
}

/**
 * Formats Excel cell values as CSV format for API
 * @param worksheetName - Name of the worksheet
 * @param values - 2D array of cell values
 * @returns CSV formatted string representation of the data
 */
function formatExcelData(worksheetName: string, values: any[][]): string {
    if (!values || values.length === 0) {
        return `Worksheet: ${worksheetName}\n\n(Empty worksheet)`;
    }
    
    // Format each row as CSV
    const csvRows: string[] = [];
    
    for (let i = 0; i < values.length; i++) {
        const row = values[i];
        if (!row || row.length === 0) continue;
        
        // Format row with CSV escaping
        const formattedRow = row.map((cell: any) => {
            // Handle null, undefined, or empty cells
            if (cell === null || cell === undefined) {
                return "";
            }
            // Convert to string
            let cellValue = String(cell);
            
            // CSV escaping: if cell contains comma, quote, or newline, wrap in quotes and escape quotes
            if (cellValue.includes(',') || cellValue.includes('"') || cellValue.includes('\n') || cellValue.includes('\r')) {
                // Escape quotes by doubling them
                cellValue = cellValue.replace(/"/g, '""');
                // Wrap in quotes
                cellValue = `"${cellValue}"`;
            }
            
            return cellValue;
        });
        
        // Join cells with comma separator
        csvRows.push(formattedRow.join(','));
    }
    
    // Combine with header text
    const csvContent = csvRows.join('\n');
    return `The current worksheet data is as follows (in CSV format):\n${csvContent}`;
}

/**
 * Inserts table data into Excel range
 * @param range - Excel range object
 * @param tableData - 2D array of cell values (array of arrays)
 */
function insertTable(range: Excel.Range, tableData: any[][]): void {
    if (!tableData || !Array.isArray(tableData) || tableData.length === 0) {
        console.warn("[Excel] Invalid table data provided");
        return;
    }
    
    const rowCount = tableData.length;
    const columnCount = tableData[0]?.length || 0;
    
    console.log(`[Excel] Inserting table with ${rowCount} rows and ${columnCount} columns`);
    
    // Get the top-left cell of the selected range and resize to match data dimensions
    const topLeftCell = range.getCell(0, 0);
    const targetRange = topLeftCell.getResizedRange(rowCount - 1, columnCount - 1);
    targetRange.values = tableData;
}

/**
 * Parses markdown table into 2D array
 * @param md - Markdown string containing table
 * @returns 2D array of table cells
 */
function parseMarkdownTable(md: string): string[][] {
    // 1. Split into lines and filter out empty lines or the separator row (---|---|---)
    const lines = md.trim().split('\n').filter(line => {
        const isSeparator = line.includes('---') && line.includes('|');
        return line.trim() !== "" && !isSeparator;
    });

    // 2. Map lines to arrays, cleaning up the pipe characters
    return lines.map(line => {
        // Remove leading/trailing pipes and split by pipe
        const cells = line.replace(/^\||\|$/g, '').split('|');
        // Trim whitespace and remove backticks (used for code formatting in markdown) from each cell
        return cells.map(cell => {
            let cleaned = cell.trim();
            // Remove leading and trailing backticks (markdown code formatting)
            cleaned = cleaned.replace(/^`+|`+$/g, '');
            return cleaned;
        });
    });
}

/**
 * Extracts markdown table from content
 * @param content - Content that may contain markdown tables
 * @returns 2D array of table cells or null if no table found
 */
function extractMarkdownTable(content: string): string[][] | null {
    const lines = content.split('\n');
    const tableLines: string[] = [];
    let inTable = false;
    let foundHeader = false;
    
    // Find consecutive lines that form a markdown table
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmedLine = line.trim();
        const hasPipes = trimmedLine.includes('|');
        const isSeparator = trimmedLine.includes('---') && hasPipes;
        
        if (hasPipes) {
            // This is a table-related line (header, separator, or data row)
            if (!inTable) {
                // Start of a new table - clear any previous partial table
                tableLines.length = 0;
                foundHeader = false;
                inTable = true;
            }
            
            if (!isSeparator) {
                // This is a header or data row
                if (!foundHeader) {
                    foundHeader = true;
                }
                tableLines.push(line);
            } else {
                // Separator row - include it to maintain table structure, will be filtered out during parsing
                tableLines.push(line);
            }
        } else if (inTable) {
            // We were in a table but this line doesn't have pipes
            // If we have at least a header and one data row, we found a complete table
            if (foundHeader && tableLines.length >= 2) {
                break;
            }
            // Otherwise, reset and continue searching
            tableLines.length = 0;
            inTable = false;
            foundHeader = false;
        }
    }
    
    // Check if we found a valid table (at least header + 1 data row, or header + separator + data rows)
    // After filtering separator, we need at least 2 rows (header + at least one data row)
    if (tableLines.length >= 2) {
        const parsed = parseMarkdownTable(tableLines.join('\n'));
        // Ensure we have at least header and one data row after parsing
        if (parsed && parsed.length >= 2) {
            return parsed;
        }
    }
    
    return null;
}

/**
 * Inserts content into Excel workbook
 * @param content - Chat message content to insert (may contain markdown tables)
 * @returns Promise that resolves when insertion is complete
 */
export async function insertIntoExcel(
    content: string
): Promise<void> {
    if (typeof Excel === 'undefined') {
        throw new Error("Excel API not available");
    }

    return Excel.run(async (context) => {
        console.log(`[Excel] Starting insert operation`);
        
        // Get the selected range
        const range = context.workbook.getSelectedRange();
        
        console.log(`[Excel] Content to process: ${content.substring(0, 200)}...`);
        
        // Try to extract markdown table from content
        const tableData = extractMarkdownTable(content);
        
        // Insert data based on type
        if (tableData && Array.isArray(tableData) && tableData.length > 0 && Array.isArray(tableData[0])) {
            console.log(`[Excel] Found markdown table with ${tableData.length} rows and ${tableData[0].length} columns`);
            insertTable(range, tableData);
        } else {
            console.log(`[Excel] No markdown table found, inserting plain text`);
            // Insert as plain text in the selected cell - truncate if too long to prevent issues
            const textToInsert = content.length > 32767 ? content.substring(0, 32767) + "..." : content;
            range.values = [[textToInsert]];
        }
        
        await context.sync();
        console.log(`[Excel] Insert operation completed successfully`);
    });
}

